<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="选择收货地址"
    width="100%"
    :before-close="handleClose"
    custom-class="address-dialog"
  >
    <div class="address-dialog-content">
      <!-- 空状态 -->
      <div v-if="!loading && addressList.length === 0" class="empty-address">
        <div class="empty-icon">📍</div>
        <p class="empty-text">暂无收货地址</p>
        <p class="empty-hint">请先添加收货地址</p>
      </div>

      <!-- 地址列表 -->
      <template v-else>
        <div
          v-for="address in addressList"
          :key="address.id"
          class="address-item"
          :class="{ 'is-selected': localSelectedId === address.id }"
          @click="selectAddress(address)"
        >
          <div class="radio-wrapper">
            <span class="radio-circle" :class="{ 'checked': localSelectedId === address.id }">
              <span v-if="localSelectedId === address.id" class="radio-inner"></span>
            </span>
          </div>

          <div class="address-info">
            <div class="contact-row">
              <span class="name">{{ address.name }}</span>
              <span class="phone">{{ address.phone }}</span>
              <span v-if="address.isDefault" class="tag-default">默认</span>
            </div>
            <div class="address-text" v-if="address.fullAddress">
              {{ address.fullAddress }}
            </div>
            <div class="address-text" v-else>
              {{ address.province }}{{ address.city }}{{ address.district }}{{ address.detail }}
            </div>
          </div>
        </div>
      </template>

      <!-- 添加新地址入口 -->
      <div
        class="add-address-entry"
        @click="goToAddAddress"
      >
        <span class="add-icon">+</span>
        <span>添加新地址</span>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <button class="btn-cancel" @click="handleClose">取消</button>
      <button class="btn-confirm" @click="handleConfirm" :disabled="!selectedAddressId">
        确认地址
      </button>
    </div>
  </el-dialog>
</template>

<script>
import { getAddressList } from '../api/address'

export default {
  name: 'SelectAddressDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selectedAddressId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      localSelectedId: null,
      loading: false,
      addressList: []
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.localSelectedId = this.selectedAddressId
        this.loadAddressList()
      }
    },
    selectedAddressId(val) {
      this.localSelectedId = val
    }
  },
  methods: {
    async loadAddressList() {
      this.loading = true

      try {
        const res = await getAddressList()

        // API返回格式：{ status:1, data: [地址数组] }
        // data直接是数组，不是 { list: [] } 格式
        const list = Array.isArray(res.data) ? res.data : []

        this.addressList = list.map(item => ({
          id: item.id,
          name: item.consignee_name,
          phone: item.consignee_phone,
          province: item.province || '',
          city: item.city || '',
          district: item.district || '',
          detail: item.detail_address || '',
          fullAddress: item.full_address || '',
          isDefault: !!item.is_default,
          createTime: item.create_time || ''
        }))

        // 如果有默认地址且未选择，自动选中默认地址
        if (!this.localSelectedId && this.addressList.length > 0) {
          const defaultAddr = this.addressList.find(addr => addr.isDefault)
          if (defaultAddr) {
            this.localSelectedId = defaultAddr.id
            this.$emit('update:selectedAddressId', defaultAddr.id)
          }
        }

        if (this.addressList.length === 0) {
          console.log('暂无收货地址')
        }
      } catch (error) {
        console.error('加载地址列表失败:', error)
        this.$message.error(error.msg || error.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    selectAddress(address) {
      this.localSelectedId = address.id
      this.$emit('update:selectedAddressId', address.id)
    },
    goToAddAddress() {
      this.$emit('update:visible', false)
      this.$router.push('/address/edit')
    },
    handleClose() {
      this.$emit('update:visible', false)
    },
    handleConfirm() {
      if (!this.localSelectedId) {
        this.$message.warning('请选择一个地址')
        return
      }

      const selectedAddress = this.addressList.find(a => a.id === this.localSelectedId)
      this.$emit('confirm', selectedAddress)
      this.handleClose()
    }
  }
}
</script>

<style scoped>
.address-dialog-content {
  max-height: 60vh;
  overflow-y: auto;
  padding-bottom: 12px;
}

/* 空状态 */
.empty-address {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-text {
  font-size: 15px;
  color: #606266;
  font-weight: 500;
  margin-bottom: 6px;
}

.empty-hint {
  font-size: 13px;
  color: #909399;
}

.address-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #fafafa;
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.address-item.is-selected {
  background: #ecf5ff;
  border-color: #409EFF;
}

.address-item:active {
  transform: scale(0.98);
}

.radio-wrapper {
  display: flex;
  align-items: flex-start;
  padding-top: 4px;
}

.radio-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #dcdfe6;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.radio-circle.checked {
  border-color: #409EFF;
  background: #409EFF;
}

.radio-inner {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
}

.address-info {
  flex: 1;
}

.contact-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.phone {
  font-size: 14px;
  color: #606266;
}

.tag-default {
  display: inline-block;
  padding: 2px 8px;
  background: #f56c6c;
  color: #fff;
  font-size: 11px;
  border-radius: 10px;
  font-weight: 500;
}

.address-text {
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
  word-break: break-all;
}

.add-address-entry {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  background: #fff;
  border: 2px dashed #dcdfe6;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #409EFF;
  font-size: 15px;
  font-weight: 500;
}

.add-address-entry:hover {
  border-color: #409EFF;
  background: #ecf5ff;
}

.add-address-entry:active {
  transform: scale(0.98);
}

.add-icon {
  font-size: 20px;
  font-weight: bold;
  line-height: 1;
}

.dialog-footer {
  display: flex;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.btn-cancel, .btn-confirm {
  flex: 1;
  height: 44px;
  border-radius: 22px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.btn-cancel {
  background: #f5f7fa;
  color: #606266;
}

.btn-cancel:active {
  transform: scale(0.95);
}

.btn-confirm {
  background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
  color: #fff;
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-confirm:not(:disabled):active {
  transform: scale(0.95);
}
</style>